'use client';

import { useMemo, useCallback, useRef, useState, useEffect } from 'react';
import { useChat } from '@ai-sdk/react';
import { DefaultChatTransport, isToolUIPart, getToolName } from 'ai';
import type { UIMessage } from 'ai';
import type { ScrapedProduct } from '@/lib/types';
import type { SearchToolResult, AgentSearchParams } from '../types';
import { toScrapedProducts } from '../types';
import { useClientConfig } from '@/lib/clients/ClientContext';

/** Find the latest searchProducts tool part with output across all messages.
 *  `toolCallId` identifies that search: it stays the same while the reply
 *  streams, so the products derived from it can too. */
function findLatestToolResult(messages: UIMessage[]): { toolCallId: string; output: SearchToolResult } | null {
  for (let i = messages.length - 1; i >= 0; i--) {
    const msg = messages[i];
    if (msg.role !== 'assistant') continue;
    for (let j = msg.parts.length - 1; j >= 0; j--) {
      const part = msg.parts[j];
      if (
        isToolUIPart(part) &&
        getToolName(part) === 'searchProducts' &&
        part.state === 'output-available' &&
        part.output
      ) {
        return { toolCallId: part.toolCallId, output: part.output as SearchToolResult };
      }
    }
  }
  return null;
}

const NO_PINS: string[] = [];

/** The reply's words are complete — the moment the chips appear. Not the end
 *  of the stream: that closes ~0.5s later, which made the grid move on its
 *  own after the reply had visibly settled. */
function replyWordsComplete(msg: UIMessage): boolean {
  const texts = msg.parts.filter(p => p.type === 'text');
  if (texts.length === 0 || texts.some(p => p.state === 'streaming')) return false;
  return !msg.parts.some(p => isToolUIPart(p) && (p.state === 'input-streaming' || p.state === 'input-available'));
}

/** The product URLs an assistant message links to, in the order it names them. */
function linkedProductUrls(msg: UIMessage): string[] {
  const text = msg.parts
    .filter((p): p is Extract<typeof p, { type: 'text' }> => p.type === 'text')
    .map(p => p.text)
    .join('');
  const urls: string[] = [];
  const linkRegex = /\[.*?\]\((https?:\/\/[^\s)]+)\)/g;
  let match;
  while ((match = linkRegex.exec(text)) !== null) {
    if (!urls.includes(match[1])) urls.push(match[1]);
  }
  return urls;
}

export function useAgentChat() {
  const clientConfig = useClientConfig();
  const apiOrigin = clientConfig.apiOrigin ?? '';
  const transport = useMemo(
    // `warehouseCode` (B2B) rides every chat turn so Αριάδνη searches the same
    // catalogue the grid shows. Sent as an extra body field, not a tool argument —
    // the model never gets to pick a warehouse. See lib/warehouseScope.ts.
    () =>
      new DefaultChatTransport({
        api: `${apiOrigin}/api/agent-chat?client=${clientConfig.clientId}`,
        ...(clientConfig.warehouseCode
          ? { body: { warehouseCode: clientConfig.warehouseCode } }
          : {}),
      }),
    [clientConfig.clientId, apiOrigin, clientConfig.warehouseCode]
  );
  // Throttled: without it every streamed token re-rendered the whole widget,
  // grid included (measured 2026-11-03: the page froze for seconds per reply).
  const chat = useChat({ transport, experimental_throttle: 50 });

  const { messages, sendMessage, status, setMessages } = chat;

  // Products of the latest search. Converted ONCE per search (keyed by its
  // toolCallId), not on every streamed token: the same array keeps flowing to
  // the grid while the reply is written, so the grid doesn't redraw for it.
  const latestSearch = findLatestToolResult(messages);
  const latestSearchKey = latestSearch ? `${clientConfig.clientId}:${latestSearch.toolCallId}` : '';
  const productsRef = useRef<{ key: string; products: ScrapedProduct[] }>({ key: '', products: [] });
  if (productsRef.current.key !== latestSearchKey) {
    productsRef.current = {
      key: latestSearchKey,
      products: latestSearch ? toScrapedProducts(latestSearch.output.products || [], clientConfig.clientId) : [],
    };
  }
  const products = productsRef.current.products;

  // The products the reply recommends (its markdown links), shown in the
  // picks row above the grid in the order the reply names them. Set ONCE, when
  // the reply is finished: pinning each link as it streamed in reshuffled the
  // top of the grid two or three times per reply (founder, 2026-11-03).
  // A new search starts unpinned; a reply without links (a question, "what
  // colours?") keeps the pins it found.
  // Pins belong to the search they were made for, so a new search never
  // inherits the previous reply's picks, not even for one frame.
  const [pins, setPins] = useState<{ searchKey: string; urls: string[] }>({ searchKey: '', urls: [] });
  useEffect(() => {
    const last = messages[messages.length - 1];
    if (!last || last.role !== 'assistant' || !replyWordsComplete(last)) return;
    const urls = linkedProductUrls(last);
    if (urls.length === 0) return;
    setPins(prev =>
      prev.searchKey === latestSearchKey && prev.urls.join('\n') === urls.join('\n')
        ? prev
        : { searchKey: latestSearchKey, urls },
    );
  }, [messages, latestSearchKey]);
  // The finished reply's links are read in the SAME render that ends
  // `awaitingPicks`, so the picks replace the placeholders in one step (via
  // the effect alone, the placeholders vanished one render before the picks
  // arrived and the grid below hopped up and back). The stored pins carry
  // them into the next turn.
  const lastMsg = messages[messages.length - 1];
  const replyLinksKey =
    lastMsg?.role === 'assistant' && replyWordsComplete(lastMsg) ? linkedProductUrls(lastMsg).join('\n') : '';
  const replyLinks = useMemo(() => (replyLinksKey ? replyLinksKey.split('\n') : null), [replyLinksKey]);
  const pinnedProductUrls = replyLinks ?? (pins.searchKey === latestSearchKey ? pins.urls : NO_PINS);

  // True while THIS turn's search results are on screen but the reply that
  // names its picks is still being written: the grid keeps placeholder cards
  // where the picks will appear (founder, 2026-11-03), instead of showing the
  // search's own top cards and swapping them out.
  const awaitingPicks =
    (status === 'submitted' || status === 'streaming') &&
    !!latestSearch &&
    lastMsg?.role === 'assistant' &&
    lastMsg.parts.some(p => isToolUIPart(p) && p.toolCallId === latestSearch.toolCallId) &&
    !replyWordsComplete(lastMsg);

  // Extract suggestion chips from [SUGGESTIONS: ...] in the latest assistant message text
  const suggestions = useMemo<string[]>(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const msg = messages[i];
      if (msg.role !== 'assistant') continue;
      const text = msg.parts
        .filter((p): p is Extract<typeof p, { type: 'text' }> => p.type === 'text')
        .map(p => p.text)
        .join('');
      const match = text.match(/\[SUGGESTIONS:\s*(.+?)\]/);
      if (match) {
        return match[1].split(',').map(s => s.trim()).filter(Boolean);
      }
      break; // only check the latest assistant message
    }
    return [];
  }, [messages]);

  // Search params from the latest tool result (for filter/pagination endpoint)
  // Stabilized: only changes reference when actual content changes,
  // preventing the filter useEffect from re-firing on every message update.
  const searchParamsKeyRef = useRef('');
  const [searchParams, setSearchParams] = useState<AgentSearchParams | null>(null);
  useEffect(() => {
    const result = findLatestToolResult(messages)?.output;
    const params = result?.searchParams ?? null;
    const key = params ? JSON.stringify(params) : '';
    if (key !== searchParamsKeyRef.current) {
      searchParamsKeyRef.current = key;
      setSearchParams(params);
    }
  }, [messages]);

  // Facet distribution — same stabilization pattern
  const facetKeyRef = useRef('');
  const [facetDistribution, setFacetDistribution] = useState<{
    category: Record<string, number>;
    brand: Record<string, number>;
  } | null>(null);
  useEffect(() => {
    const result = findLatestToolResult(messages)?.output;
    const facets = result?.facetDistribution ?? null;
    const key = facets ? JSON.stringify(facets) : '';
    if (key !== facetKeyRef.current) {
      facetKeyRef.current = key;
      setFacetDistribution(facets);
    }
  }, [messages]);

  // Check if a tool call is currently in progress
  const isToolCalling = useMemo(() => {
    if (messages.length === 0) return false;
    const lastMsg = messages[messages.length - 1];
    if (lastMsg.role !== 'assistant') return false;
    return lastMsg.parts.some(
      part =>
        isToolUIPart(part) &&
        (part.state === 'input-streaming' || part.state === 'input-available')
    );
  }, [messages]);

  const isLoading = status === 'streaming' || status === 'submitted';

  // Chip click sends a new message
  const handleChipClick = useCallback(
    (chip: string) => {
      sendMessage({ text: `Δείξε μου: ${chip}` });
    },
    [sendMessage]
  );

  // Send a text message
  const handleSend = useCallback(
    (text: string) => {
      if (!text.trim()) return;
      sendMessage({ text });
    },
    [sendMessage]
  );

  const clearChat = useCallback(() => {
    setMessages([]);
  }, [setMessages]);

  return {
    messages,
    products,
    /** Identifies the search behind `products`; changes only with a new search. */
    searchKey: latestSearchKey,
    pinnedProductUrls,
    awaitingPicks,
    suggestions,
    searchParams,
    facetDistribution,
    isLoading,
    isToolCalling,
    sendMessage: handleSend,
    handleChipClick,
    clearChat,
  };
}
